/**
 * Things people say to the assistant while a draft is waiting for an answer, which must never be saved as
 * that answer: switching to another kind, starting over, or asking for it to go out later.
 * (Cancelling in other words lives in cancelPhrasing.ts, with the other ways of cancelling.)
 */

type Kind = "notice" | "poll" | "event";

const KIND_WORDS: Array<[RegExp, Kind]> = [
  [/\bpolls?\b|\bvote\b/i, "poll"],
  [/\b(?:calendar\s+)?events?\b|\bon\s+the\s+calendar\b/i, "event"],
  [/\b(?:normal|plain|regular|simple|info(?:rmation)?|ordinary|basic)\s+(?:notice|one|announcement|message)\b|\bnotices?\b|\bannouncement\b/i, "notice"],
];

const kindIn = (words: string): Kind | undefined => KIND_WORDS.find(([pattern]) => pattern.test(words))?.[1];

/**
 * "no wait, I want a poll", "switch to a poll", "change it back to a poll", "can we make this a calendar event instead?",
 * "actually just make it a normal notice", "no, a plain notice is better", "rather a poll": the kind they want instead.
 */
const SWITCHES = [
  /^(?:(?:no|sorry|actually|wait)[,\s]+)+(?:a\s+|an\s+)?(poll|event|notice)\s+after\s+all[.!?]*$/i,
  /^(?:(?:no|wait|actually|sorry|hmm+|oh|hold on|oops|ah)[,.!\s]+)+(?:i\s+(?:want|need|meant|wanted)|let'?s\s+(?:do|make)|make\s+(?:it|this)|do|rather|just|it\s+should\s+be|it'?s)\s+(?:(?:just|rather)\s+)?(?:a|an)\s+(.+?)(?:\s+instead)?(?:\s+(?:please|then))?[.!?]*$/i,
  /^(?:(?:please|can\s+you|could\s+you|actually|just|ok(?:ay)?)[,\s]+)*(?:switch|change|turn|convert|make)\s+(?:it|this|that)?\s*(?:back\s+)?(?:in)?to\s+(?:a|an)\s+(.+?)(?:\s+instead)?(?:\s+please)?[.!?]*$/i,
  /^(?:(?:actually|no|ok(?:ay)?|hmm+)[,\s]+)*(?:can|could)\s+(?:we|you)\s+(?:make|do|turn)\s+(?:it|this|that)\s+(?:into\s+)?(?:a|an)\s+(.+?)(?:\s+instead)?[.!?]*$/i,
  /^(?:(?:actually|no|ok(?:ay)?)[,\s]+)*(?:just\s+)?make\s+(?:it|this)\s+(?:a|an)\s+(.+?)(?:\s+instead)?[.!?]*$/i,
  /^(?:(?:no|actually|nah)[,.!\s]+)+(?:a|an)\s+(.+?)\s+(?:is|would\s+be)\s+better[.!?]*$/i,
  /^(?:(?:no|actually|nah)[,.!\s]+)*(?:rather|instead)\s+(?:a|an)\s+(.+?)[.!?]*$/i,
];

/** The kind a message switches the open draft to, if it's asking to switch. */
export function switchedKind(said: string): Kind | undefined {
  const text = said.trim();
  if (/^(?:wait[, ]+)?it'?s actually an event with a date[, ]+can it go on the calendar[?.!]*$/i.test(text)) return "event";
  for (const pattern of SWITCHES) {
    const what = text.match(pattern)?.[1];
    // "a poll", "calendar event", "normal notice": the words after "a" name a kind, and nothing much more
    // "make it an urgent notice" is about urgency, not a switch
    if (what && what.split(/\s+/).length <= 3 && !/\burgent\b/i.test(what)) {
      const kind = kindIn(what);
      if (kind) return kind;
    }
  }
  return undefined;
}

/** "start over", "restart", "let's start again", "from scratch please". */
const START_OVER =
  /^(?:(?:ok(?:ay)?|no|actually|ugh|sorry|right|let'?s|can\s+we|could\s+we|please)[,.!\s]+)*(?:(?:wipe\s+(?:it|this)\s+and\s+)?start(?:\s+(?:it|this))?\s+(?:over|again|afresh|from\s+scratch|from\s+the\s+(?:top|beginning))|restart(?:\s+(?:it|this))?|begin\s+again|redo\s+(?:it|this|the\s+whole\s+thing)|from\s+scratch)(?:\s+please)?[.!?]*$/i;

export const startsOver = (said: string): boolean => START_OVER.test(said.trim()) || /^start (?:the|my|this) (?:notice|poll|event) again[.!?]*$/i.test(said.trim());

/**
 * "send it tomorrow at 7am", "can it go out on Monday morning?", "post it at 6 tonight", "publish this next week":
 * a time to send, which the chat can't set. Returns when it should go, as said.
 */
const SEND_LATER =
  /^(?:(?:please|can\s+you|could\s+you|and|also|oh)[,\s]+)*(?:(?:send|post|publish|schedule|make\s+it\s+go\s+out)\s+(?:it|this|that|the\s+(?:notice|poll|event))|(?:can|could|will)\s+(?:it|this|that)\s+(?:go\s+out|be\s+sent|be\s+posted|be\s+published|send))\s+((?:on|at|for|by|in|from|this|next|tomorrow|tonight|today|first\s+thing|early|later|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b.*?)[.!?]*$/i;

export function sendsLater(said: string): string | undefined {
  const when = said.trim().match(/^make\s+it\s+go\s+out\s+(.+)$/i)?.[1]?.trim() ?? said.trim().match(SEND_LATER)?.[1]?.trim();
  // "send it to grade 4" is who, not when
  return when && !/^(?:to|for)\s+(?!tomorrow|tonight|today|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next|this|\d)/i.test(when) ? when : undefined;
}
